import { environmentHttpClient as httpClient } from "client/Api/httpClient";

export enum AfasResourceKind {
	MyTasks = 1,
	MySignals = 2
}

export enum AfasResultStatus {
	GeneralFailure = 0,
	Success = 1,
	NoAccess = 2,
	RequireNewToken = 3
}

export type ErrorResultStatus = AfasResultStatus.GeneralFailure
	| AfasResultStatus.NoAccess
	| AfasResultStatus.RequireNewToken;

export type AfasConnectionInterests = {
	items?: true;
};

export type AfasLiveInformationRequestModel = {
	resourceKind: AfasResourceKind;
	connectionId: number;
	interestedIn: AfasConnectionInterests;
};

export type TaskListItem = {
	id: number;
	title: string;
	type: string;
	action: string;
	submitDate: string | Date;
	navigationUrl: string;
};

export type SignalListItem = {
	id: number;
	description: string;
	type: string;
	creationDate: string | Date;
};

export type AfasListItem<TResourceKind extends AfasResourceKind> =
	TResourceKind extends AfasResourceKind.MyTasks ? TaskListItem :
	TResourceKind extends AfasResourceKind.MySignals ? SignalListItem :
	never;

export type AfasLiveInformationResponseModelForResourceKind<TResourceKind extends AfasResourceKind> = {
	connectionId: number;
	resourceKind: TResourceKind;
} & ({
	status: AfasResultStatus.Success;
	items: LazyLoadPageResult<AfasListItem<TResourceKind>>;
} | {
	status: ErrorResultStatus;
});

export type MyTasksAfasLiveInformationResponseModel = AfasLiveInformationResponseModelForResourceKind<AfasResourceKind.MyTasks>;

export type MySignalsAfasLiveInformationResponseModel = AfasLiveInformationResponseModelForResourceKind<AfasResourceKind.MySignals>;

export type AfasLiveInformationResponseModel = MyTasksAfasLiveInformationResponseModel | MySignalsAfasLiveInformationResponseModel;

export type AfasListModel<TResourceKind extends AfasResourceKind> = {
	status: AfasResultStatus.Success;
	items:LazyLoadPageResult<AfasListItem<TResourceKind>>;
} | {
	status: ErrorResultStatus;
};

export type MyTasksListModel = AfasListModel<AfasResourceKind.MyTasks>;
export type MySignalsListModel = AfasListModel<AfasResourceKind.MySignals>;

export type RequestNewTokenModel = {
	connectionId: number;
	oneTimePassword: string;
};

export enum AfasOneTimePasswordResponseKind {
	RequestCreated = 0,
	RequireEmailAddress = 1
}

export type AfasOneTimePasswordResponseModel = {
	kind: AfasOneTimePasswordResponseKind;
};

export function getLiveInformation(requestModels: AfasLiveInformationRequestModel[]): Promise<AfasLiveInformationResponseModel[]> {
	return httpClient.post<AfasLiveInformationResponseModel[]>("Workspace/LiveInformation/Afas", requestModels);
}

export function getMyTasks(
	connectionId: number,
	skip: number,
	amount: number,
	searchTerm: string,
	signal?: AbortSignal
): Promise<MyTasksListModel> {
	return httpClient.get<MyTasksListModel>(
		"Workspace/LiveInformation/Afas/MyTasks",
		{
			connectionId,
			skip,
			amount,
			searchTerm
		},
		{ signal }
	);
}

export function getMySignals(
	connectionId: number,
	skip: number,
	amount: number,
	searchTerm: string,
	signal?: AbortSignal
): Promise<MySignalsListModel> {
	return httpClient.get<MySignalsListModel>(
		"Workspace/LiveInformation/Afas/MySignals",
		{
			connectionId,
			skip,
			amount,
			searchTerm
		},
		{ signal }
	);
}

export function requestOneTimePassword(
	connectionId: number,
	signal?: AbortSignal
): Promise<AfasOneTimePasswordResponseModel> {
	return httpClient.post<AfasOneTimePasswordResponseModel>(
		"Workspace/LiveInformation/Afas/Token/Otp",
		{ connectionId },
		{ signal }
	);
}

export function requestNewToken(
	model: RequestNewTokenModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/LiveInformation/Afas/Token",
		model,
		{ signal }
	);
}
